import type { ClientProfile } from '../../data/types'
import { displayName } from '../../data/mockClients'
import { getClientExtras } from '../../data/clientExtras'
import { Icon, type IconName } from '../../components/Icon'
import { useEffect, useState } from 'react'
import { getPreference } from '../../api/preferences'

function ageFromDob(dob?: string): string {
  if (!dob) return ''
  const m = dob.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/)
  if (!m) return ''
  const [, mm, dd, yyyy] = m
  const birth = new Date(Number(yyyy), Number(mm) - 1, Number(dd))
  if (Number.isNaN(birth.getTime())) return ''
  const now = new Date()
  let age = now.getFullYear() - birth.getFullYear()
  const md = now.getMonth() - birth.getMonth()
  if (md < 0 || (md === 0 && now.getDate() < birth.getDate())) age--
  return `${age} yrs`
}

type Field = { label: string; value?: string; setting?: string }

function Section({
  title,
  icon,
  fields,
  checked,
}: {
  title: string
  icon: IconName
  fields: Field[]
  checked: Record<string, boolean> | null
}) {
  const visibleFields = checked ? fields.filter((field) => !field.setting || checked[field.setting] !== false) : fields
  return (
    <div className="card">
      <div className="card__head">
        <span className="fs-ic">
          <Icon name={icon} size={18} />
        </span>
        <h3>{title}</h3>
      </div>
      <div className="card__body">
        <div className="facts">
          {visibleFields.map((f) => (
            <div key={f.label} className="fact">
              <span className="fact__k">{f.label}</span>
              <span className={`fact__v${f.value ? '' : ' is-empty'}`}>
                {f.value || 'Not on file'}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export function FaceSheetTab({ client }: { client: ClientProfile }) {
  const [checked, setChecked] = useState<Record<string, boolean> | null>(null)
  useEffect(() => {
    getPreference<{ checked: Record<string, boolean> }>('clients.facesheet')
      .then((saved) => setChecked(saved?.checked || null))
      .catch(() => setChecked(null))
  }, [])
  const x = getClientExtras(client.id)
  const p = (key: string): string | undefined => {
    const value = client.profile?.[key]
    return typeof value === 'string' && value.trim() ? value : undefined
  }
  const cityLine = [p('cli_city') || x.city, p('cli_state') || x.state].filter(Boolean).join(', ')
  const addressBlock = client.address || [p('cli_address') || x.address, [cityLine, p('cli_zip') || x.zip].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(' · ')

  return (
    <div className="fs-grid" style={{ marginTop: '1rem' }}>
      <Section
        title="Demographics"
        icon="user"
        checked={checked}
        fields={[
          { label: 'Full name', value: displayName(client), setting: 'CLIENT NAME' },
          { label: 'Date of birth', value: client.dob, setting: 'DATE OF BIRTH' },
          { label: 'Age', value: ageFromDob(client.dob) },
          { label: 'Gender', value: client.gender || p('cli_sex') || x.gender, setting: 'SEX' },
          { label: 'Marital status', value: p('cli_material_status') || x.maritalStatus, setting: 'MARITAL STATUS' },
          { label: 'Primary language', value: p('cli_primary_lang') || x.primaryLanguage, setting: 'PRIMARY LANGUAGE' },
          { label: 'Status', value: client.status },
        ]}
      />

      <Section
        title="Contact"
        icon="phone"
        checked={checked}
        fields={[
          { label: 'Cell phone', value: client.phoneCell, setting: 'CELL PHONE' },
          { label: 'Home phone', value: client.phoneHome, setting: 'HOME PHONE' },
          { label: 'Email', value: client.email || p('cli_email') || x.email, setting: 'CLIENT EMAIL ID' },
          { label: 'Address', value: addressBlock, setting: 'ADDRESS 1' },
        ]}
      />

      <Section
        title="Coverage & IDs"
        icon="id"
        checked={checked}
        fields={[
          { label: 'Client ID', value: client.id, setting: 'CLIENT ID' },
          { label: 'Medicaid ID', value: client.medicaidId || p('cli_medicaid') || x.medicaidId, setting: 'MEDICAID ID' },
          { label: 'Medicare ID', value: client.medicareId || p('cli_medicare') || x.medicareId, setting: 'MEDICARE ID' },
          { label: 'Payer', value: client.payerName, setting: 'PAYER NAME' },
        ]}
      />

      <Section
        title="Care team"
        icon="clinical"
        checked={checked}
        fields={[
          { label: 'Primary physician', value: x.physicianName, setting: 'PRIMARY PHYSICIAN' },
          { label: 'Physician phone', value: x.physicianPhone, setting: 'PHYSICIAN PHONE' },
          { label: 'Physician fax', value: x.physicianFax, setting: 'PHYSICIAN FAX' },
          { label: 'Care manager', value: client.careManager || p('cli_mltss_manager_name'), setting: 'MLTSS MANAGER NAME' },
          { label: 'Program', value: client.program || p('cli_program_name'), setting: 'PROGRAM NAME' },
          { label: 'Admission date', value: client.admissionDate || p('cli_adm_date') || x.admissionDate, setting: 'ADMISSION DATE' },
        ]}
      />
    </div>
  )
}
